import $ from 'jquery';
import { v1 } from 'ember-uuid';
import { getOwner } from '@ember/application';
import { set } from '@ember/object';
import { htmlSafe } from '@ember/template';
import { later } from '@ember/runloop';
import scrollIntoView from 'scroll-into-view-if-needed';

import { showModal } from './modal-controls';

const allErrorsPresent = (errors, formErrors) => {
  if (!errors) {
    return false;
  }

  const formErrorsMap = formErrors.reduce((accum, key) => {
    accum[key] = true;
    return accum;
  }, { });

  for (const key of Object.keys(errors)) {
    if (!formErrorsMap[key]) {
      return false;
    }
  }

  return true;
};

/**
 * Move any unaccounted for errors to a specified key.
 * Traditionally errors are shown using a modal.
 * This applies to error handling inside modals/overlays.
 *
 * @param errors
 * @param formErrors
 * @param newKey
 * @returns
 */
const mergeMissingErrorsIntoKey = (errors, formErrors, newKey = null) => {
  // no need to modify
  if (!newKey) {
    return errors;
  }

  // track all errors that are being specifically handled in UI
  const formErrorsMap = formErrors.reduce((accum, key) => {
    accum[key] = true;
    return accum;
  }, { });

  return Object.keys(errors).reduce((accum, key) => {
    // allow unhandled errors to merged into the specified key
    if (!formErrorsMap[key]) {
      if (!accum[newKey]) {
        accum[newKey] = [];
      }

      // merge errors into designated key
      if (Array.isArray(errors[key])) {
        accum[newKey].push(...errors[key]);
      } else {
        accum[newKey].push(errors[key]);
      }
    } else {
      // keep as it was
      accum[key] = errors[key];
    }
    return accum;
  }, {});
};

/**
 * Clear any set errors
 * @param scope
 */
export const clearErrors = (scope) => {
  set(scope, 'errors', { });
};

/**
 * @param scope
 * @param adapterError
 * @param formErrors
 * @param opt_isScrollToElement
 * @param opt_customBodyMessage
 * @param opt_debugMessage
 */
export const handleFormErrorOrModal = (scope, adapterError, formErrors, opt_isScrollToElement, opt_customBodyMessage, opt_debugMessage) => {
  const { errors } = adapterError;

  if (!allErrorsPresent(errors, formErrors)) {
    handleErrorResponseWithModal(scope, adapterError, opt_customBodyMessage, opt_debugMessage);
    return;
  }

  handleErrorResponse(scope, adapterError, formErrors, opt_isScrollToElement);
};

/**
 * @param scope
 * @param adapterError
 * @param errorKeys
 * @param opt_isScrollToErrorKey
 * @param opt_isInModal
 * @param opt_customErrorKey
 * @param opt_unhandledErrorsKey
 */
export const handleErrorResponse = (scope, adapterError, errorKeys, opt_isScrollToErrorKey, opt_isInModal, opt_customErrorKey, opt_unhandledErrorsKey) => {
  const { errors } = adapterError;

  const finalizedErrors = mergeMissingErrorsIntoKey(errors, errorKeys, opt_unhandledErrorsKey);

  if (finalizedErrors) {
    setErrors(scope, finalizedErrors, errorKeys, opt_isScrollToErrorKey, opt_isInModal, opt_customErrorKey);
  }
};

/**
 * @param scope
 * @param adapterError
 * @param formErrors
 * @param unhandledErrorKey
 * @param opt_isScrollToElement
 * @param opt_customErrorKey
 */
export const handleFormErrorInOverlay = (
  scope,
  adapterError,
  formErrors,
  unhandledErrorKey,
  opt_isScrollToElement,
  opt_customErrorKey,
) => {
  handleErrorResponse(scope, adapterError, formErrors, opt_isScrollToElement, null, opt_customErrorKey, unhandledErrorKey);
};

/**
 * @param scope
 * @param adapterError
 * @param opt_customBodyMessage
 * @param opt_debugOnly
 * @param opt_title
 */
export const handleErrorResponseWithModal = (scope, adapterError, opt_customBodyMessage, opt_debugOnly, opt_title) => {
  const errorTracker = getOwner(scope).lookup('service:error-tracker');
  const errorId = v1();

  let errorMessage = opt_customBodyMessage
    ? `Error id : ${errorId} -- Message : ${opt_customBodyMessage}`
    : `Error id : ${errorId}`;

  if (opt_debugOnly) {
    errorMessage += ` -- Debug: ${opt_debugOnly}`;
  }

  errorTracker.error(errorMessage, scope, adapterError);
  console.error('search error id: ' + errorId);

  const errors = adapterError.errors || { };
  const errorData = Object.values(errors).flat();
  const bodyText = htmlSafe(errorData.join('<br>').trim() || opt_customBodyMessage || 'Please try again');
  const titleText = opt_title || 'There were some errors';

  showModal(scope, 'alert', {
    bodyText,
    titleText,
    actionButtonText: 'OK',
  });
};

/**
 * @param scope
 * @param errors
 * @param opt_errorListArr
 * @param opt_isScrollToError
 * @param opt_isInModal
 */
export const setErrors = (scope, errors, opt_errorListArr, opt_isScrollToError, opt_isInModal, opt_customErrorKey) => {
  const errorKey = opt_customErrorKey ?? 'errors';
  set(scope, errorKey, errors);

  if (!opt_errorListArr && !opt_isScrollToError) {
    later(null, () => {
      showGenericErrorModal(scope, errors);
    });
    return;
  }

  if (!opt_isScrollToError) {
    return;
  }

  later(null, () => {
    let errorOrder = opt_errorListArr || [];

    let firstErrorElTop;
    let firstErrorEl;

    for (let i = 0; i < errorOrder.length; i++) {
      const errorName = errorOrder[i];
      const error = errors[errorName];

      if (error) {
        const errorEl = $(`.error.${errorName}`);
        if (!errorEl.position()) {
          //This error element doesn't exist, let's log it and show the generic error popover.
          console.error('Error from server, but no input set to display it %o', error);
          showGenericErrorModal(scope, errors);
          return;
        }

        // Pick the first error element on the page to scroll to
        const errorElTop = errorEl.offset().top;
        if (!firstErrorElTop || errorElTop < firstErrorElTop) {
          firstErrorElTop = errorElTop;
          firstErrorEl = errorEl;
        }
      }
    }

    scrollIntoView(firstErrorEl.get(0), {
      scrollMode: 'if-needed',
      block: 'center',
      inline: 'nearest',
    });
  }, 100);
};

/**
 * @param scope
 * @param errors
 * @param opt_titleText
 */
export const showGenericErrorModal = (scope, errors, opt_titleText) => {
  console.error('User Error, but no handler defined. Errors: %o ', errors);

  const lineBreaks = '<br><br>';
  let errorText = '';

  if (typeof errors === 'string') {
    errorText = errors;
  } else {
    const errorValues = [];
    Object.keys(errors).forEach((errorKey) => {
      const errorValue = errors[errorKey];
      errorValues.push(
        typeof errorValue === 'string' ? errorValue : errorValue.join(lineBreaks)
      );
    });
    errorText = errorValues.join(lineBreaks);
  }

  if (!errorText) {
    errorText = 'Please try again';
  }

  let modalData = {
    titleText: opt_titleText ? opt_titleText : 'Error',
    bodyText: errorText,
    actionButtonText: 'OK'
  };

  showModal(scope, 'alert', modalData);
};
